import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { SECTIONS, COMMENTARY, COMMENTARY_ART, MOMENT_ORDER } from "@/data/content";
import CommentaryArt from "./CommentaryArt";

const SEGMENT_SECONDS = 4; // placeholder timing for moments without real audio
const SPEED_STEPS = [1, 1.25, 1.5, 2];

export default function CommentaryOverlay() {
  const { commentaryId, closeCommentary, openCommentary } = useMatch();
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const moment = commentaryId ? SECTIONS[commentaryId] : null;
  const data = commentaryId ? COMMENTARY[commentaryId] : null;
  const hasRealAudio = Boolean(data?.audioUrl);
  const lines = data ? data.lines : [];

  const placeholderTotal = lines.length * SEGMENT_SECONDS;
  const totalSeconds = hasRealAudio ? duration : placeholderTotal;

  const activeLine = hasRealAudio
    ? Math.max(0, lines.reduce((acc, l, i) => (elapsed >= l.start ? i : acc), 0))
    : Math.min(Math.floor(elapsed / SEGMENT_SECONDS), Math.max(lines.length - 1, 0));
  const momentIndex = Math.max(0, MOMENT_ORDER.indexOf(commentaryId));

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const teardownAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  // Reset when a different moment opens / overlay closes
  useEffect(() => {
    setElapsed(0);
    setPlaying(false);
    setDuration(0);
    setSpeed(1);
    setAudioError(false);
    stopTimer();
    teardownAudio();
  }, [commentaryId, stopTimer, teardownAudio]);

  useEffect(() => {
    return () => {
      stopTimer();
      teardownAudio();
    };
  }, [stopTimer, teardownAudio]);

  // Real-audio: track timeupdate; placeholder: manual timer (speed-aware)
  useEffect(() => {
    if (hasRealAudio) return;
    if (playing && elapsed < totalSeconds) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 0.1 * speed), 100);
    } else {
      stopTimer();
      if (elapsed >= totalSeconds && totalSeconds > 0) setPlaying(false);
    }
    return stopTimer;
  }, [playing, elapsed, totalSeconds, hasRealAudio, speed, stopTimer]);

  const togglePlay = () => {
    if (hasRealAudio) {
      if (!audioRef.current) {
        const a = new Audio(data.audioUrl);
        a.addEventListener("loadedmetadata", () => setDuration(a.duration || 0));
        a.addEventListener("timeupdate", () => setElapsed(a.currentTime));
        a.addEventListener("ended", () => setPlaying(false));
        a.addEventListener("error", () => {
          setAudioError(true);
          setPlaying(false);
        });
        a.playbackRate = speed;
        a.muted = muted;
        audioRef.current = a;
      }
      const a = audioRef.current;
      if (playing) {
        a.pause();
        setPlaying(false);
      } else {
        a.play();
        setPlaying(true);
      }
    } else {
      setPlaying((p) => !p);
    }
  };

  const cycleSpeed = () => {
    const next = SPEED_STEPS[(SPEED_STEPS.indexOf(speed) + 1) % SPEED_STEPS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const toggleMute = () => {
    setMuted((m) => {
      if (audioRef.current) audioRef.current.muted = !m;
      return !m;
    });
  };

  const seekTo = (e) => {
    if (totalSeconds <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const t = ratio * totalSeconds;
    setElapsed(t);
    if (hasRealAudio && audioRef.current) {
      audioRef.current.currentTime = t;
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const progress = totalSeconds > 0 ? elapsed / totalSeconds : 0;

  return (
    <AnimatePresence>
      {moment && data && (
        <motion.div
          key="commentary-overlay"
          data-testid="commentary-overlay"
          className="fixed inset-0 z-[80] pitch-stripes flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* close */}
          <button
            type="button"
            onClick={closeCommentary}
            data-testid="commentary-close-btn"
            className="absolute top-20 sm:top-24 right-4 sm:right-8 z-10 bg-white border-[3px] border-black hard-shadow px-4 py-2 font-broadcast font-extrabold text-xs tracking-[0.2em] uppercase text-black transition-all duration-150 hover:-translate-y-0.5 hover:shadow-[6px_6px_0_0_#000]"
          >
            ✕ Close
          </button>

          <div className="flex-1 overflow-y-auto pt-24 sm:pt-28 pb-10 px-4 sm:px-8 lg:px-16 flex items-start lg:items-center">
            <div className="max-w-[1300px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* media slot */}
              <div className="relative">
                <div className="relative aspect-[16/10] border-4 border-black hard-shadow-lg bg-[#0A0A0A] overflow-hidden">
                  <CommentaryArt momentId={commentaryId} />
                </div>
                <span className="inline-block mt-4 bg-white border-2 border-black px-3 py-1.5 font-broadcast font-bold text-[10px] tracking-[0.2em] uppercase text-black" data-testid="commentary-art-label">
                  {COMMENTARY_ART[commentaryId]?.label}
                </span>
              </div>

              {/* commentary panel */}
              <div className="bg-white border-[3px] border-black hard-shadow-lg p-5 sm:p-7" data-testid="commentary-panel">
                {/* header */}
                <div className="flex items-center justify-between border-b-2 border-black/10 pb-3 mb-5">
                  <span className="flex items-center gap-2 font-broadcast font-extrabold text-[11px] sm:text-xs tracking-[0.3em] uppercase text-black">
                    <span className="w-2 h-2 rounded-full bg-[#84E116] live-dot" />
                    Commentary
                  </span>
                  <span
                    className="font-broadcast font-bold text-[11px] sm:text-xs tracking-[0.25em] uppercase text-black/50"
                    data-testid="commentary-counter"
                  >
                    {String(momentIndex + 1).padStart(2, "0")} / {String(MOMENT_ORDER.length).padStart(2, "0")}
                  </span>
                </div>

                {/* heading */}
                <p className="font-broadcast font-bold text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-black/45 mb-1.5">
                  Replay · {moment.minuteEvent}
                </p>
                <h3 className="font-display uppercase text-lg sm:text-xl leading-tight tracking-tight text-black mb-5">
                  {moment.headline}
                </h3>

                {/* single-sentence transcript (full plain list if audio fails) */}
                <div className="min-h-[132px] sm:min-h-[148px]" data-testid="commentary-transcript">
                  {audioError ? (
                    <div data-testid="commentary-transcript-plain">
                      <p className="font-broadcast font-bold text-[10px] tracking-[0.2em] uppercase text-black/50 mb-3">
                        Audio unavailable · transcript shown as text
                      </p>
                      {lines.map((line, i) => (
                        <p key={i} className="text-sm sm:text-base font-medium leading-relaxed text-black/80 py-1.5">
                          {line.text}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <>
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={activeLine}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.25 }}
                          className="text-base sm:text-lg font-bold leading-snug text-black bg-[#DFFBA6] border-l-4 border-[#84E116] px-4 py-3"
                          data-testid="commentary-sentence-active"
                        >
                          {lines[activeLine]?.text}
                        </motion.p>
                      </AnimatePresence>
                      <p
                        className="mt-3 text-sm sm:text-base font-medium leading-snug text-black/35 px-1 min-h-[40px]"
                        data-testid="commentary-sentence-next"
                      >
                        {lines[activeLine + 1]?.text || ""}
                      </p>
                    </>
                  )}
                </div>

                {/* player */}
                <div className="bg-[#0A0A0A] rounded-xl px-4 sm:px-5 pt-3.5 pb-3 mt-4 ring-1 ring-black/80 shadow-[0_2px_0_0_rgba(0,0,0,0.9)]">
                  <div className="flex items-center gap-3 sm:gap-3.5">
                    <button
                      type="button"
                      onClick={togglePlay}
                      data-testid="commentary-play-btn"
                      className="shrink-0 w-10 h-10 rounded-full border-2 border-[#84E116] flex items-center justify-center text-[#84E116] transition-all duration-150 hover:bg-[#84E116] hover:text-black"
                      aria-label={playing ? "Pause" : "Play"}
                    >
                      {playing ? (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><rect x="6" y="4" width="4.5" height="16" rx="1" /><rect x="13.5" y="4" width="4.5" height="16" rx="1" /></svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-4 h-4 ml-0.5" fill="currentColor"><path d="M7 4.5 L19 12 L7 19.5 Z" /></svg>
                      )}
                    </button>

                    {/* waveform + seek */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer py-1"
                      onClick={seekTo}
                      data-testid="commentary-waveform"
                      role="slider"
                      aria-label="Seek"
                      aria-valuemin={0}
                      aria-valuemax={Math.round(totalSeconds)}
                      aria-valuenow={Math.round(elapsed)}
                    >
                      <div className="flex items-center gap-[2px] h-9" aria-hidden="true">
                        {Array.from({ length: 48 }).map((_, i) => {
                          const filled = i / 48 <= progress;
                          const t = (i / 48) * Math.PI * 2;
                          const v = 0.42 + 0.32 * Math.abs(Math.sin(t * 3.1 + 0.4)) + 0.2 * Math.abs(Math.sin(t * 7.3 + 1.2)) + 0.06 * Math.sin(t * 13.7);
                          return (
                            <span
                              key={i}
                              className={`flex-1 rounded-full transition-colors duration-150 ${filled ? "bg-[#84E116]" : "bg-[#333333]"}`}
                              style={{ height: `${Math.max(14, Math.round(v * 100))}%` }}
                            />
                          );
                        })}
                      </div>
                      <div className="relative h-[3px] bg-[#2E2E2E] mt-2 rounded-full">
                        <div className="absolute left-0 top-0 h-full bg-[#84E116] rounded-full" style={{ width: `${progress * 100}%` }} />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-[#84E116]"
                          style={{ left: `${progress * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* speed + volume */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={cycleSpeed}
                        data-testid="commentary-speed-btn"
                        className="min-w-[44px] text-center font-broadcast font-bold text-[10px] tracking-widest text-[#84E116]/90 border border-[#2E2E2E] hover:border-[#84E116]/60 hover:text-[#84E116] rounded-full px-2.5 py-1 transition-colors duration-150"
                      >
                        {speed}×
                      </button>
                      <button
                        type="button"
                        onClick={toggleMute}
                        data-testid="commentary-volume-btn"
                        className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-[#84E116] transition-colors duration-150"
                        aria-label={muted ? "Unmute" : "Mute"}
                      >
                        {muted ? (
                          <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.6 3l2.7-2.7-1.4-1.4-2.7 2.7-2.7-2.7-1.4 1.4 2.7 2.7-2.7 2.7 1.4 1.4 2.7-2.7 2.7 2.7 1.4-1.4-2.7-2.7z" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z" /></svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2.5">
                    <span className="font-broadcast font-bold text-[10px] tracking-widest text-[#84E116]" data-testid="commentary-elapsed">{fmt(elapsed)}</span>
                    <span className="font-broadcast font-bold text-[10px] tracking-widest text-white/45" data-testid="commentary-duration">{fmt(totalSeconds)}</span>
                  </div>
                </div>

                {/* moment pagination dots */}
                <div className="flex items-center justify-center gap-2.5 mt-5" data-testid="commentary-dots">
                  {MOMENT_ORDER.map((id, i) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => openCommentary(id)}
                      data-testid={`commentary-dot-${i}`}
                      aria-label={`Go to moment ${i + 1}`}
                      className={`rounded-full border-2 transition-all duration-150 ${
                        i === momentIndex
                          ? "w-2.5 h-2.5 bg-[#84E116] border-black"
                          : "w-2.5 h-2.5 bg-white border-black hover:bg-[#84E116]/40"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
